import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, Users, BarChart3, RefreshCw, Monitor, X, Pause, ExternalLink, QrCode, Copy, Check } from 'lucide-react';
import { TCLIconImage } from '../../components/TCLMark';
import { useFacilitateAuth } from '../../context/FacilitateAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BAND_COLORS = {
  Developing: 'bg-slate-500/20 text-slate-300',
  Emerging: 'bg-amber-500/20 text-amber-300',
  Established: 'bg-blue-500/20 text-blue-300',
  Advanced: 'bg-emerald-500/20 text-emerald-300',
};

const BAND_DOT = {
  Developing: 'bg-slate-400',
  Emerging: 'bg-amber-400',
  Established: 'bg-blue-400',
  Advanced: 'bg-emerald-400',
};

const PILLAR_SHORT = {
  self_anchoring_band: 'SA',
  perspective_shifting_band: 'PS',
  adaptive_communication_band: 'AC',
  relational_integration_band: 'RI',
};

const ProjectorCard = ({ share, index }) => (
  <div
    className="bg-white/[0.04] border border-white/10 rounded-2xl p-5"
    style={{ animationDelay: (index * 60) + 'ms' }}
    data-testid={`projector-card-${share.id}`}
  >
    <p className="text-[#FAF8F3] font-serif text-lg font-medium mb-4 truncate">{share.display_name}</p>
    <div className="space-y-2">
      {[
        { key: 'self_anchoring_band', label: 'Self-Anchoring' },
        { key: 'perspective_shifting_band', label: 'Perspective-Shifting' },
        { key: 'adaptive_communication_band', label: 'Adaptive Comm.' },
        { key: 'relational_integration_band', label: 'Relational Int.' },
      ].map(({ key, label }) => {
        const band = share[key];
        return (
          <div key={key} className="flex items-center justify-between gap-2">
            <span className="text-[#FAF8F3]/30 text-xs truncate">{label}</span>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${BAND_COLORS[band] || 'bg-white/5 text-white/30'}`}>
              {band}
            </span>
          </div>
        );
      })}
    </div>
  </div>
);

const FacilitateRoom = () => {
  const { sessionCode } = useParams();
  const navigate = useNavigate();
  const { token, loading: authLoading } = useFacilitateAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projecting, setProjecting] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const participantUrl = `${window.location.origin}/cfq-assessment?session=${sessionCode}`;

  const fetchRoom = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/sessions/facilitate/room/${sessionCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setData(await res.json());
      } else if (res.status === 401) {
        navigate('/facilitate/login');
      }
    } catch {}
    setLoading(false);
  }, [sessionCode, token, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!token) { navigate('/facilitate/login'); return; }
    fetchRoom();
    // Auto-refresh every 10 seconds for live sessions
    const interval = setInterval(fetchRoom, 10000);
    return () => clearInterval(interval);
  }, [fetchRoom, token, navigate, authLoading]);

  const handleClose = async () => {
    if (!window.confirm('Close this session? Shared data will be archived.')) return;
    const csrf = document.cookie.match(/(?:^|;\s*)tcl_csrf=([^;]+)/);
    const csrfToken = csrf ? decodeURIComponent(csrf[1]) : null;
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    if (csrfToken) h['X-CSRF-Token'] = csrfToken;
    try {
      await fetch(`${API_URL}/api/sessions/facilitate/room/${sessionCode}/close`, {
        method: 'POST',
        headers: h,
        credentials: 'include',
      });
      fetchRoom();
    } catch {}
  };

  if (loading) return <div className="min-h-screen bg-[#071B3A] flex items-center justify-center text-[#FAF8F3]/30">Loading...</div>;
  if (!data) return <div className="min-h-screen bg-[#071B3A] flex items-center justify-center text-[#FAF8F3]/30">Session not found</div>;

  const { session, shares, pulse_count, share_count } = data;
  const isLive = session.status === 'live';

  // Projector view
  if (projecting) {
    return (
      <div className="min-h-screen bg-[#071B3A] flex flex-col overflow-hidden" data-testid="projector-view">
        {/* Top bar */}
        <div className="flex items-center justify-between px-10 py-5 border-b border-white/5">
          <div className="flex items-center gap-4">
            <TCLIconImage size={40} />
            <div>
              <p className="text-[#C99316] text-[10px] font-medium tracking-[0.2em] uppercase">Cultural Fluency Quotient</p>
              <h1 className="font-serif text-2xl text-[#FAF8F3] leading-tight">{session.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-10">
            <div className="text-center">
              <p className="text-5xl font-light text-[#FAF8F3]">{pulse_count}</p>
              <p className="text-[#FAF8F3]/30 text-xs mt-1 tracking-wide uppercase">Completed</p>
            </div>
            <div className="text-center">
              <p className="text-5xl font-light text-[#C99316]">{share_count}</p>
              <p className="text-[#FAF8F3]/30 text-xs mt-1 tracking-wide uppercase">Shared</p>
            </div>
            <div className="text-center">
              <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${isLive ? 'bg-emerald-400 animate-pulse' : 'bg-white/20'}`} />
              <p className={`text-xs tracking-wide uppercase ${isLive ? 'text-emerald-400' : 'text-white/20'}`}>{isLive ? 'Live' : 'Closed'}</p>
            </div>
          </div>
        </div>

        {/* Participant cards grid */}
        <div className="flex-1 overflow-y-auto px-8 py-8">
          {shares.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center">
              <div className="bg-white rounded-2xl p-4 mb-6 shadow-xl">
                <img
                  src={"https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=" + encodeURIComponent(participantUrl)}
                  alt="Participant QR code"
                  width={220}
                  height={220}
                  className="block"
                />
              </div>
              <p className="text-[#FAF8F3]/50 text-xl font-serif font-light mb-2">Waiting for participants…</p>
              <p className="text-[#FAF8F3]/20 text-sm font-mono">{sessionCode}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {shares.map((s, idx) => (
                <ProjectorCard key={s.id} share={s} index={idx} />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-10 py-3 border-t border-white/5 flex items-center justify-between">
          <p className="text-[#FAF8F3]/10 text-xs font-mono">{window.location.origin}/cfq-assessment?session={session.code}</p>
          <button onClick={() => setProjecting(false)} className="text-[#FAF8F3]/20 hover:text-[#FAF8F3]/50 text-xs flex items-center gap-1">
            <X size={14} /> Exit projector
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#071B3A]" data-testid="facilitate-room">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>

      {/* QR Code Overlay */}
      {showQR && (
        <div
          className="fixed inset-0 bg-[#071B3A]/95 z-50 flex flex-col items-center justify-center p-8 cursor-pointer"
          onClick={() => setShowQR(false)}
          data-testid="qr-overlay"
        >
          <button
            onClick={() => setShowQR(false)}
            className="absolute top-5 right-6 text-white/30 hover:text-white/70"
            data-testid="qr-close-btn"
          >
            <X size={22} />
          </button>

          <p className="text-[#C99316]/60 text-[11px] font-medium tracking-[0.25em] uppercase mb-6">Scan to take the CFQ</p>

          {/* QR image */}
          <div className="bg-white rounded-2xl p-5 shadow-2xl mb-8" onClick={e => e.stopPropagation()} data-testid="qr-image-wrapper">
            <img
              src={"https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=" + encodeURIComponent(participantUrl)}
              alt="Participant QR code"
              width={300}
              height={300}
              className="block"
            />
          </div>

          {/* Session name */}
          {session && (
            <h2 className="font-serif text-3xl text-[#FAF8F3] mb-2 text-center">{session.name}</h2>
          )}

          {/* URL + copy */}
          <div
            className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-5 py-3 mt-2"
            onClick={e => e.stopPropagation()}
          >
            <span className="text-[#FAF8F3]/40 text-sm font-mono truncate max-w-xs">{participantUrl}</span>
            <button
              onClick={() => { navigator.clipboard.writeText(participantUrl); setUrlCopied(true); setTimeout(() => setUrlCopied(false), 2000); }}
              className="text-[#FAF8F3]/30 hover:text-[#C99316] shrink-0"
              data-testid="qr-copy-url-btn"
            >
              {urlCopied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
            </button>
            <button
              onClick={async (e) => {
                e.stopPropagation();
                const qrSrc = "https://api.qrserver.com/v1/create-qr-code/?size=800x800&data=" + encodeURIComponent(participantUrl);
                try {
                  const res = await fetch(qrSrc);
                  const blob = await res.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = "CFQ-QR-" + sessionCode + ".png";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                } catch {
                  window.open(qrSrc, '_blank');
                }
              }}
              className="text-[#FAF8F3]/30 hover:text-[#C99316] shrink-0"
              title="Download QR as PNG"
              data-testid="qr-download-btn"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </button>
          </div>

          <p className="text-white/15 text-xs mt-6">Click anywhere to dismiss</p>
        </div>
      )}
      {/* Header */}
      <div className="border-b border-white/5 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/facilitate" className="text-[#FAF8F3]/30 hover:text-[#FAF8F3]/60"><ArrowLeft size={18} /></Link>
            <div>
              <h1 className="font-serif text-lg text-[#FAF8F3]">{session.name}</h1>
              <p className="text-[#FAF8F3]/20 text-xs font-mono">{session.code} · {session.status}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={`/cfq-assessment?session=${session.code}&test=1`} target="_blank" rel="noopener noreferrer" className="px-3 py-1.5 border border-[#C99316]/20 text-[#C99316]/60 text-xs rounded-lg hover:border-[#C99316]/40 hover:text-[#C99316] flex items-center gap-1.5" data-testid="test-session-btn">
              <ExternalLink size={14} /> Test this session
            </a>
            <button
              onClick={() => setShowQR(true)}
              className="px-3 py-1.5 border border-white/10 text-[#FAF8F3]/40 text-xs rounded-lg hover:border-[#C99316]/20 hover:text-[#C99316] flex items-center gap-1.5"
              data-testid="show-qr-btn"
            >
              <QrCode size={14} /> Show QR
            </button>
            <button onClick={fetchRoom} className="p-2 text-[#FAF8F3]/30 hover:text-[#FAF8F3]/60" title="Refresh"><RefreshCw size={16} /></button>
            <button onClick={() => setProjecting(true)} className="px-3 py-1.5 border border-white/10 text-[#FAF8F3]/40 text-xs rounded-lg hover:border-[#C99316]/20 hover:text-[#C99316] flex items-center gap-1.5" data-testid="project-btn">
              <Monitor size={14} /> Project this
            </button>
            {isLive && (
              <button onClick={handleClose} className="px-3 py-1.5 border border-red-500/20 text-red-400/60 text-xs rounded-lg hover:border-red-500/40 hover:text-red-400 flex items-center gap-1.5" data-testid="close-room-btn">
                <Pause size={14} /> Close Room
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5 text-center">
            <BarChart3 size={16} className="text-[#C99316]/40 mx-auto mb-2" />
            <p className="text-3xl font-light text-[#FAF8F3]">{pulse_count}</p>
            <p className="text-[#FAF8F3]/20 text-xs mt-1">Completed</p>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5 text-center">
            <Users size={16} className="text-[#C99316]/40 mx-auto mb-2" />
            <p className="text-3xl font-light text-[#C99316]">{share_count}</p>
            <p className="text-[#FAF8F3]/20 text-xs mt-1">Shared with you</p>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5 text-center">
            <div className={`w-3 h-3 rounded-full mx-auto mb-2 ${isLive ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
            <p className="text-3xl font-light text-[#FAF8F3]">{isLive ? 'Live' : session.status}</p>
            <p className="text-[#FAF8F3]/20 text-xs mt-1">Status</p>
          </div>
        </div>

        {/* Shared Participants */}
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6">
          <h2 className="text-[#FAF8F3]/60 text-sm font-semibold mb-4">People who shared with you</h2>
          {shares.length === 0 ? (
            <p className="text-[#FAF8F3]/20 text-sm py-6 text-center">
              {isLive ? 'Waiting for participants to share...' : 'No participants shared in this session.'}
            </p>
          ) : (
            <div className="space-y-3">
              {shares.map((s) => (
                <div key={s.id} className="border border-white/5 rounded-lg p-4" data-testid={`share-${s.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[#FAF8F3] font-medium">{s.display_name}</p>
                    <p className="text-[#FAF8F3]/15 text-xs">{new Date(s.created_at).toLocaleTimeString()}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: 'Self-Anchoring', band: s.self_anchoring_band },
                      { label: 'Perspective-Shifting', band: s.perspective_shifting_band },
                      { label: 'Adaptive Comm.', band: s.adaptive_communication_band },
                      { label: 'Relational Int.', band: s.relational_integration_band },
                    ].map((p) => (
                      <div key={p.label} className="flex items-center gap-1.5">
                        <span className="text-[#FAF8F3]/25 text-[10px]">{p.label}:</span>
                        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${BAND_COLORS[p.band] || 'bg-white/5 text-white/30'}`}>{p.band}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FacilitateRoom;
